import { DateRangePicker, Tooltip, getDate } from '@systemeio/ui'
import { CONTACT_API } from 'modules/contacts/api/contact-api'
import { ContactTagsActionEnum } from 'modules/contacts/contact-page/enums/contact-tags-enum'
import { useContactTags } from 'modules/contacts/contact-page/hooks/use-contact-tags'
import { ContactPageFilterInterface } from 'modules/contacts/contact-page/types/contact-page-filter-interface'
import { ContactTagsInterface } from 'modules/contacts/contact-page/types/contact-tags-interface'
import { ContactId, ContactInterface } from 'modules/contacts/types/contact-interface'
import React, { useEffect } from 'react'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useUpdateData } from 'shared/hooks/use-update-data'
import useUser from 'shared/hooks/use-user'
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination'
import { OrderEnum } from 'shared/types/pagination-interface'
import { dateFormat, dateTimeFormat } from 'shared/utils/date-time-format'
import { getDateRangeSinceDate, getToday } from 'shared/utils/get-dates'

interface ContactTagsProps {
  contactId: ContactId
  contact?: ContactInterface
  shouldUpdateTags: boolean
}

const defaultFilters: ContactPageFilterInterface = { date: [null, null] }

const ContactTags = ({ contactId, contact, shouldUpdateTags }: ContactTagsProps) => {
  const { t } = useLocoTranslation()
  const { user, intlLocale } = useUser()
  const { setPagination, pagination, setLimit, limit, filter, setFilter } = useFilterPagination(
    defaultFilters,
    true,
  )

  const updateTagData = useUpdateData({
    api: `${CONTACT_API}/contacts/${contactId}/tags`,
    customApi: `${CONTACT_API}/contacts/${contactId}/tags`,
    setPagination,
  })
  const { contactTags } = useContactTags({
    contactId,
    limit,
    pagination,
    filter,
    shouldFetch: filter.date[0] !== null,
  })

  useEffect(() => {
    if (shouldUpdateTags) {
      updateTagData(true, contactTags)
    }
  }, [contactTags, shouldUpdateTags, updateTagData])

  useEffect(() => {
    if (contact) {
      setFilter({ date: getDateRangeSinceDate(contact.createdAt, user?.timezone) })
    }
  }, [contact, setFilter, user?.timezone])

  const columns: ColumnDefinitionInterface<ContactTagsInterface>[] = [
    {
      key: 'tag',
      header: t('dashboard.contact_page.tag'),
      widthPercent: 50,
      onRender: tag => (
        <Tooltip wrapperClassName={'w-full'} mode={'overflow'} label={tag.tag.name}>
          {tag.tag.name}
        </Tooltip>
      ),
    },
    {
      key: 'tag_action',
      header: t('global.action'),
      widthPercent: 30,
      onRender: tag => <span>{t(ContactTagsActionEnum[tag.action])}</span>,
    },
    {
      key: 'date',
      header: t('global.date'),
      widthPercent: 20,
      onRender: tag => (
        <Tooltip
          wrapperClassName={'w-full'}
          mode={'hover'}
          label={dateTimeFormat(tag.date, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })}
        >
          <span className={'w-full truncate'}>
            {dateFormat(tag.date, {
              dateFormat: user?.dateFormat,
              timeZone: user?.timezone,
            })}
          </span>
        </Tooltip>
      ),
      onRenderSmall: tag => (
        <div className={'flex items-center gap-3'}>
          <span className={'text-text-primary font-bold'}>{t('global.date')}</span>
          <span>
            {dateTimeFormat(tag.date, {
              timeFormat: user?.timeFormat,
              dateFormat: user?.dateFormat,
              timeZone: user?.timezone,
            })}
          </span>
        </div>
      ),
    },
  ]
  return (
    <ListingLayout
      counter={false}
      data={contactTags}
      type={'default'}
      title={<span className={'text-[17px]'}>{t('dashboard.contact_page.tag_history')}</span>}
      customHeaderElement={
        <DateRangePicker
          maxDate={getToday(user?.timezone)}
          minDate={getDate(contact?.createdAt, user?.timezone)}
          getDefaultDate={() => getDateRangeSinceDate(contact?.createdAt, user?.timezone)}
          date={filter.date}
          onChange={date => {
            setPagination({ startFromId: undefined, order: OrderEnum.next })
            setLimit(10)
            setFilter({ date: date })
          }}
          timezone={user?.timezone}
          createdAt={user?.createdAt}
          intlLocale={intlLocale}
        />
      }
    >
      <Table
        limiter={{ limit, setLimit }}
        paginator={{ pagination, setPagination }}
        data={contactTags}
        columns={columns}
      />
    </ListingLayout>
  )
}

export default ContactTags
